import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Sparkles, Headphones, ArrowRight, CheckCircle2, Truck, Home as HomeIcon, MapPin, Navigation, Landmark, ShoppingBag, ShoppingCart } from "lucide-react";
import { useLang, localized } from "../i18n/LanguageContext";
import { SEO_PREFIX } from "../i18n/translations";
import useSEO from "../hooks/useSEO";
import api from "../lib/api";
import BookingEngine from "../components/BookingEngine";
import ProductCard from "../components/ProductCard";
import Reviews from "../components/Reviews";
import { Button } from "../components/ui/button";

const Home = () => {
  const { lang, tr, lp } = useLang();
  const [products, setProducts] = useState([]);
  const [cities, setCities] = useState([]);
  useSEO({
    title: "MobiRider — " + tr.hero.title,
    description: tr.hero.subtitle,
    lang,
    jsonLd: { "@context": "https://schema.org", "@type": "LocalBusiness", name: "MobiRider", description: tr.hero.subtitle, areaServed: cities.map((c) => c.name) },
  });
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/cities").then((r) => setCities(r.data));
  }, []);

  const steps = [
    { icon: CheckCircle2, t: tr.steps.s1t, d: tr.steps.s1d },
    { icon: Truck, t: tr.steps.s2t, d: tr.steps.s2d },
    { icon: Sparkles, t: tr.steps.s3t, d: tr.steps.s3d },
    { icon: HomeIcon, t: tr.steps.s4t, d: tr.steps.s4d },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/14479602/pexels-photo-14479602.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1400" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-10 md:pt-24 md:pb-16">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 bg-brand/10 text-brand font-semibold px-4 py-2 rounded-full text-sm mb-6">
              <MapPin className="w-4 h-4" /> {cities.length ? cities.map((c) => c.name).join(" · ") : "Toulouse · Marseille"}
            </span>
            <h1 className="font-heading font-extrabold text-4xl md:text-5xl lg:text-6xl text-ink tracking-tight leading-[1.05]">
              {tr.hero.title}
            </h1>
            <p className="text-xl md:text-2xl text-ink-muted mt-6 leading-relaxed">{tr.hero.subtitle}</p>
            <div className="flex flex-wrap gap-5 mt-7 text-base font-semibold text-ink">
              <span className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-brand" /> {tr.hero.trust1}</span>
              <span className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-brand" /> {tr.hero.trust2}</span>
              <span className="flex items-center gap-2"><Headphones className="w-5 h-5 text-brand" /> {tr.hero.trust3}</span>
            </div>
          </div>
          <div className="mt-10 max-w-5xl">
            <BookingEngine />
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="py-14 md:py-20 bg-white" data-testid="usecases-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-ink tracking-tight">{tr.usecases.title}</h2>
            <p className="text-xl text-ink-muted mt-4">{tr.usecases.subtitle}</p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {tr.usecases.items.map((item, i) => {
              const Icon = [Navigation, Landmark, ShoppingBag, ShoppingCart][i];
              return (
                <div key={i} className="flex flex-col items-center text-center bg-cream rounded-3xl p-7 border border-slate-100 animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                  <div className="w-16 h-16 rounded-2xl bg-brand/10 grid place-items-center mb-4"><Icon className="w-8 h-8 text-brand" /></div>
                  <span className="text-lg font-semibold text-ink leading-snug">{item}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 md:py-24" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-ink tracking-tight text-center mb-14">{tr.steps.title}</h2>
          <div className="grid gap-6 md:grid-cols-4">
            {steps.map((s, i) => (
              <div key={i} className="relative bg-white rounded-3xl p-7 border border-slate-100 shadow-md animate-fade-up" style={{ animationDelay: `${i * 90}ms` }}>
                <div className="w-14 h-14 rounded-2xl bg-brand text-white grid place-items-center mb-5"><s.icon className="w-7 h-7" /></div>
                <span className="absolute top-6 right-7 font-heading font-extrabold text-4xl text-slate-100">{i + 1}</span>
                <h3 className="font-heading font-bold text-xl text-ink mb-2">{s.t}</h3>
                <p className="text-base text-ink-muted leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="py-16 md:py-24 bg-white" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-ink tracking-tight">{tr.products.title}</h2>
            <p className="text-xl text-ink-muted mt-4">{tr.products.subtitle}</p>
          </div>
          <div className="grid gap-7 md:grid-cols-3">
            {products.slice(0, 3).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
          <div className="text-center mt-10">
            <Link to={lp("/scooters")}><Button variant="outline" className="rounded-full h-14 px-8 border-2 font-bold text-lg">{tr.products.viewAll} <ArrowRight className="w-5 h-5 ml-2" /></Button></Link>
          </div>
        </div>
      </section>

      {/* DESTINATIONS */}
      <section className="py-16 md:py-24" data-testid="dest-section">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-ink tracking-tight mb-10">{tr.destPage.title}</h2>
          <div className="grid gap-7 md:grid-cols-2">
            {cities.map((c) => (
              <Link key={c.id} to={lp(`/${SEO_PREFIX[lang]}-${c.slug}`)} data-testid={`dest-${c.slug}`}
                className="group relative rounded-3xl overflow-hidden h-64 shadow-md">
                <img src={c.hero_image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
                <div className="absolute bottom-0 left-0 p-7">
                  <h3 className="font-heading font-extrabold text-3xl text-white">{c.name}</h3>
                  <span className="text-white/90 flex items-center gap-2 mt-2 text-lg font-semibold">{tr.cityPage.bookHere} {c.name} <ArrowRight className="w-5 h-5" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Reviews />

      {/* CTA */}
      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-brand rounded-[2rem] p-10 md:p-14 text-center text-white hero-grain">
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl mb-4">{tr.cta.question}</h2>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <Link to={lp("/reservation")}><Button className="rounded-full h-14 px-8 bg-accent2 hover:bg-accent2-hover text-white font-bold text-lg">{tr.cta.bookNow}</Button></Link>
              <Link to={lp("/contact")}><Button className="rounded-full h-14 px-8 bg-white text-brand hover:bg-slate-100 font-bold text-lg">{tr.nav.contact}</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
